/**
 * MOT TRANG = BON LOP CHONG NHAU, cung mot he toa do.
 *
 *   +-----------------------------------------+
 *   |  SVG chu thich        (tren cung, bat su kien)
 *   |  DOM van ban trong suot (chon / Ctrl+F / trinh doc man hinh)
 *   |  Lop chinh sua        (che khoi cu, ve chu moi - xem EditedTextLayer)
 *   |  Canvas raster        (duoi cung, chi la pixel cua TAI LIEU GOC)
 *   +-----------------------------------------+
 *
 * Bon lop nam chinh xac chong khit nhau. Moi lop deu duoc dat kich thuoc tu
 * cung mot phep tinh trong geometry.ts - khong lop nao tu tinh lay kich thuoc
 * cua rieng no.
 *
 * Trinh soan tai cho KHONG nam trong .pageSurface: be mat trang cat bot phan
 * tran (overflow: hidden), ma bang phan hoi cua trinh soan phai duoc phep tran
 * ra ngoai mep trang. No la anh em cua .pageSurface, cung goc toa do.
 */
import { useCallback, useMemo } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type {
  Annotation,
  DocumentId,
  PdfCore,
  PdfPoint,
  PdfRect,
  TextBlock,
} from '@pdfeditor/types';
import { CanvasLayer } from './CanvasLayer';
import { TextLayer } from './TextLayer';
import { AnnotationLayer, type AnnotationCapture } from './AnnotationLayer';
import { NotePopup, type TextBoxDraftStyle } from './NotePopup';
import { EditedTextLayer, type EditRenderHint, type EditedItem } from './EditedTextLayer';
import { EmbeddedImageLayer, type EmbeddedImageView } from './EmbeddedImageLayer';
import { useEmbeddedImages, useImageDragOverlay } from './useEmbeddedImages';
import type { ProjectedImage } from '../core/content/projection';
import { pickImageAt } from '../core/content/embeddedImages';
import { cssPointToPdf, displaySize, pdfRectToCss, type PageGeometry } from './geometry';
import { usePageProxy, usePageTextBlocks } from './usePage';
import { InlineTextEditor, type EditSession } from '../ui/InlineTextEditor';
import { cssColor, runsColorRgb, runsFontSizePt, type TextAlign } from '../ui/textEditCore';
import { resolveScope, scopeAtPoint, type EditScope } from '../core/edit/scope';
import { isShapeTool, type ShapeTool, type ToolId } from '../state';

export interface PageViewProps {
  core: PdfCore | null;
  /**
   * Tai lieu pdf.js CUA O TRANG NAY - khong nhat thiet la tai lieu dang mo.
   *
   * Sau khi ghep tep, cac o trang lien ke nhau co the thuoc hai tai lieu khac
   * nhau. Doi tuong trang phai lay tu dung tai lieu cua o do; lay nham thi trang
   * ghep se hien ra la mot trang cua ban goc, va do la loi KHONG THE nhin ra
   * tren man hinh neu hai tep co bo cuc giong nhau.
   */
  doc: DocumentId | null;
  /** Vi tri trong so do trang. Day la so trang nguoi dung thay (+1). */
  slotIndex: number;
  /** Chi so trang trong `doc`. null = trang trang, khong co gi de ve. */
  sourcePageIndex: number | null;
  /**
   * Chi so trang trong TAI LIEU GOC, hoac null neu o nay khong tu tai lieu goc.
   *
   * Sua chu va chu thich song trong khong gian chi so nay (ma khoi "p3-b7",
   * `Annotation.pageIndex`), nen o trang khong thuoc tai lieu goc thi hai tinh
   * nang do TAT hoan toan o day. Cho chung chay tren chi so cua tai lieu khac
   * se sua nham trang - im lang va dung vao trang nguoi dung khong nhin.
   */
  originalPageIndex: number | null;
  geometry: PageGeometry;
  /** Trang nam trong vung nhin (hoac vung dem) - duoc phep nap va ve. */
  active: boolean;
  /** Nguoi dung dang cuon nhanh: ve ban nhap, tam tat lop van ban. */
  draft: boolean;
  annotations: readonly Annotation[];
  selectedBlockId: string | null;
  /** Pham vi dang chon. Chi ve vien khi no thuoc ve trang nay. */
  selectedScope: EditScope | null;
  onSelectBlock: (block: TextBlock | null, scope: EditScope | null) => void;
  onBlocksLoaded: (pageIndex: number, blocks: TextBlock[]) => void;
  hasTextLayer: boolean;

  /** Cong cu dang bat. 'text' bien mot cu bam thanh mot phien soan. */
  tool: ToolId;
  /** Phien soan dang mo, hoac null. Chi ve khi no thuoc ve trang nay. */
  editSession: EditSession | null;
  /** Chieu cua cac lenh text.replace dang co hieu luc (chu + kieu chu). */
  edited: ReadonlyMap<string, EditedItem>;
  editHints: ReadonlyMap<string, EditRenderHint>;
  onRequestEdit: (block: TextBlock, scope: EditScope) => void;
  /**
   * Tang moi lan lich su chinh sua doi. Di vao chu ky ve cua canvas de mot lan
   * sua khong bao gio de lai pixel cu. Xem ghi chu trong CanvasLayer.
   */
  editRevision: number;

  // --- Chu thich ------------------------------------------------------------
  /** Mot net ve tay da xong (da lam muot), toa do PDF. */
  onInkStroke?: (pageIndex: number, path: PdfPoint[]) => void;
  /** Nguoi dung bam dat mot ghi chu dinh moi, toa do PDF. */
  onNotePoint?: (pageIndex: number, point: PdfPoint) => void;
  /** Bam vao mot dau da co - dung de chon no va mo o soan neu no co chu. */
  onSelectAnnotation?: (id: string) => void;
  /** Doi tuong dang chon - ve tay nam va vien chon quanh no. */
  selectedAnnotationId?: string | null;
  /** Bam vao cho trong voi cong cu Chọn - bo chon doi tuong. */
  onDeselectAnnotation?: () => void;
  /**
   * Hop chu nhat cua mot doi tuong da doi: keo di cho, hoac keo tay nam.
   *
   * `rect` DA di qua resolveResize()/moveRect() cua core/annotations/shapes.ts,
   * tuc la da chuan hoa, da ap san kich thuoc, va voi hop chu thi da do lai
   * chieu cao. Ben goi chi viec phat mot lenh annotation.update - khong duoc
   * tinh lai gi o day, vi day chinh la cho hai phep tinh se troi khoi nhau.
   */
  onChangeAnnotationRect?: (id: string, rect: PdfRect) => void;
  /** Xoa doi tuong dang chon (phim Delete tren lop chu thich). */
  onDeleteAnnotation?: (id: string) => void;
  /** Keo xong mot hinh khoi. Hop chu nhat da o he toa do PDF. */
  onShape?: (tool: ShapeTool, pageIndex: number, rect: PdfRect) => void;
  /**
   * Nguoi dung da chi cho dat anh: keo mot khung, hoac chi BAM mot cai.
   *
   * `rect` la null khi nguoi dung chi bam - luc do ben goi dat anh o co tu nhien
   * lay diem bam lam goc tren-trai. Phep tinh do nam trong placementRect() cua
   * core/annotations/images.ts, KHONG o day: lop hien thi ve tu chinh
   * `annotation.rect`, nen mot phep tinh thu hai o tang nay se la mot ban xem
   * truoc noi doi.
   */
  onImageRect?: (pageIndex: number, rect: PdfRect | null, point: PdfPoint) => void;
  /**
   * Dia chi hien thi cua mot anh da ghi nhan, tra cuu theo `Annotation.imageRef`.
   *
   * Chuyen thang xuong AnnotationLayer. Tang nay khong giu byte anh - chung song
   * trong ImageStore cua loi, va App tao dia chi tu do.
   */
  imageUrl?: (ref: string) => string | undefined;
  /** O chu giai dang mo, neu no thuoc ve trang nay. */
  noteDraft?: NoteDraft | null;
  /** Kieu chu LIVE cua o chu giai, do App giu (bang thuoc tinh o popup sua). */
  noteStyle?: TextBoxDraftStyle;
  onNoteSave?: (text: string) => void;
  /** Phim tat can le trong o go goi vao day de doi kieu chu o App. */
  onNoteAlignChange?: (align: TextAlign) => void;
  onNoteCancel?: () => void;

  /** Anh da nhung san trong trang - xem viewer/useEmbeddedImages.ts. */
  embeddedImages?: EmbeddedImageBridge;
}

/**
 * CAU NOI CHO ANH DA NHUNG SAN.
 *
 * Gom vao MOT doi tuong thay vi bay prop roi: bay prop nay phai di qua ca
 * VirtualizedDocument, va moi lan them mot thao tac anh se la mot vong sua ba
 * tep. Quan trong hon, gom lai thi ta thay ngay tinh nang nay la mot don vi -
 * ai muon tat no chi can khong truyen prop.
 */
export interface EmbeddedImageBridge {
  /** Danh sach lenh dang co hieu luc. Vi tri anh duoc SUY RA tu day. */
  commands: readonly import('@pdfeditor/types').EditCommand[];
  selectedId: string | null;
  onSelect: (image: ProjectedImage | null) => void;
  /** Nguoi dung nha chuot sau mot cu keo. */
  onCommitRect: (image: ProjectedImage, rect: PdfRect) => void;
  /** Bam trung do hoa vecto - can noi that la khong sua duoc. */
  onVectorNote?: (note: string) => void;
  /** Trang nay khong sua anh duoc, kem ly do. */
  onRefusal?: (reason: string) => void;
}

/** Mot o soan dang mo: doi tuong chu moi (id === null) hoac doi tuong da co. */
export interface NoteDraft {
  id: string | null;
  /**
   * Loai doi tuong chu.
   *
   * 'text-note' la ghi chu (giay vang, co nen); 'free-text' la hop chu tu do
   * (khong nen). Ca hai dung chung mot o soan vi thao tac giong het nhau - chi
   * khac hinh thuc cua thu ghi ra.
   */
  kind: 'text-note' | 'free-text';
  pageIndex: number;
  point: PdfPoint;
  value: string;
  /**
   * Kieu chu ma o soan mo ra VOI.
   *
   * Doi tuong da co thi day la kieu chu that cua no; doi tuong moi thi la mac
   * dinh. O soan cho doi cac gia tri nay ngay trong luc go va tra chung ve theo
   * onNoteSave - nen mot hop chu dat xong dam, nghieng, co chu va can le chi
   * trong MOT buoc, dung nhu duong sua chu san co.
   */
  style: TextBoxDraftStyle;
}

/** Danh sach lenh rong dung chung - de useMemo trong hook khong doi moi khung hinh. */
const EMPTY_COMMANDS: readonly import('@pdfeditor/types').EditCommand[] = [];

export function PageView({
  core,
  doc,
  slotIndex,
  sourcePageIndex,
  originalPageIndex,
  geometry,
  active,
  draft,
  annotations,
  selectedBlockId,
  selectedScope,
  onSelectBlock,
  onBlocksLoaded,
  hasTextLayer,
  tool,
  editSession,
  edited,
  editHints,
  onRequestEdit,
  editRevision,
  onInkStroke,
  onNotePoint,
  onSelectAnnotation,
  selectedAnnotationId,
  onDeselectAnnotation,
  onChangeAnnotationRect,
  onDeleteAnnotation,
  onShape,
  onImageRect,
  imageUrl,
  noteDraft,
  noteStyle,
  onNoteSave,
  onNoteAlignChange,
  onNoteCancel,
  embeddedImages,
}: PageViewProps) {
  const size = displaySize(geometry);
  const isBlank = sourcePageIndex === null;
  const page = usePageProxy(core, doc, sourcePageIndex ?? 0, active && !isBlank);

  /**
   * Trich van ban CHI cho trang cua tai lieu goc.
   *
   * Ma khoi mang chi so trang cua tai lieu goc ("p3-b7"), va toan bo duong sua
   * chu - tu lua chon pham vi den lenh text.replace - deu dua vao dieu do. Trich
   * van ban cua mot trang ghep se sinh ra ma khoi trung ten voi khoi cua trang
   * goc cung so, va lan sua tiep theo se ghi vao nham trang.
   */
  const editable = originalPageIndex !== null;
  // Chi trich van ban khi trang dung yen - day la thao tac dat nhat cua trang.
  const blocks = usePageTextBlocks(
    core,
    doc,
    originalPageIndex ?? 0,
    active && !draft && editable,
    onBlocksLoaded,
  );

  const selected = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) ?? null,
    [blocks, selectedBlockId],
  );

  /** Pham vi dang chon, neu no thuoc ve trang nay. */
  const scopeHere = useMemo(() => {
    if (!selectedScope || selectedScope.pageIndex !== originalPageIndex) return null;
    return selectedScope;
  }, [selectedScope, originalPageIndex]);

  /**
   * Vien DAM: dung don vi se bi sua. Vien MO: ca khoi do viec gom nhom tao ra.
   *
   * Hai vien nay phai cung xuat hien. Chi ve vien dam thi nguoi dung khong biet
   * con co the noi rong ra toi dau; chi ve vien mo thi ho tuong minh sap sua ca
   * bang - dung noi so ma tinh nang nay sinh ra de go bo.
   */
  const highlightRect = useMemo(
    () => (scopeHere ? pdfRectToCss(scopeHere.bbox, geometry) : null),
    [scopeHere, geometry],
  );

  const blockOutlineRect = useMemo(() => {
    if (!selected) return null;
    if (scopeHere && scopeHere.kind === 'block') return null; // trung vien dam
    return pdfRectToCss(selected.bbox, geometry);
  }, [selected, scopeHere, geometry]);

  /** Pham vi dang duoc soan, neu phien soan thuoc ve trang nay. */
  const editingScope = useMemo(() => {
    if (!editSession || editSession.pageIndex !== originalPageIndex) return null;
    const parent = blocks.find((b) => b.id === editSession.parentBlockId);
    if (!parent) return null;
    return resolveScope(parent, editSession.scopeId);
  }, [editSession, originalPageIndex, blocks]);

  /**
   * Bam vao trang -> chon DON VI NHO NHAT nam duoi con tro.
   *
   * Hai buoc: tim khoi chua diem bam (duyet nguoc de khoi ve sau nam tren, dung
   * nhu tru tu ve), roi trong khoi do tim run -> dong -> ca khoi.
   *
   * Buoc thu hai la thu sua duoc trai nghiem tren tai lieu nhieu bang: khoi co
   * the la ca mot bang, nhung cai nguoi dung bam vao la MOT O.
   *
   * Voi cong cu "Sửa chữ", cu bam do mo luon trinh soan tai cho: mot thao tac,
   * khong phai chon roi tim nut sua o dau do.
   */
  /**
   * =========================================================================
   *  MOT LOP BAT THAO TAC DUY NHAT, MOT CHE DO
   * =========================================================================
   *
   *  Truoc day o day co HAI lop chong nhau: lop cua AnnotationLayer (biet chon,
   *  di chuyen, doi kich thuoc) va mot ShapeCaptureSurface rieng cua tep nay
   *  (chi biet ve hinh, va tinh hinh hoc bang mot ban sao thu hai cua
   *  shapes.ts). Ket qua la duong ve va duong thao tac khong bao gio gap nhau:
   *  keo ra duoc mot hinh, roi khong doi kich thuoc no duoc.
   *
   *  Bay gio chi con mot lop, va no doi CHE DO theo cong cu. Moi phep tinh hinh
   *  hoc - hop tu cu keo, vung bam, vi tri tay nam, ket qua doi kich thuoc -
   *  deu di qua core/annotations/shapes.ts, dung cac ham ma duong ghi goi.
   */
  const capture = useMemo((): AnnotationCapture => {
    // Chu thich bam vao chi so trang cua TAI LIEU GOC. Tren trang ghep va trang
    // trang khong co chi so do, nen lop nay TAT hoan toan - khong con duong nao
    // sinh ra mot chu thich tro toi trang khong ton tai.
    if (originalPageIndex === null) return 'none';
    if (isShapeTool(tool)) return tool;
    if (tool === 'ink') return 'ink';
    // Cong cu "Chèn ảnh" co che do RIENG chu khong muon che do hinh khoi: mot
    // cu BAM khong keo phai co nghia ("dat o co tu nhien"), trong khi voi hinh
    // khoi cu bam do la mot hinh rong va bi bo qua.
    if (tool === 'image') return 'image';
    // "Hộp chữ" dung CHUNG che do 'note': ca hai deu la "bam mot diem roi go
    // chu". Khac biet nam o thu duoc TAO RA, khong o cach bat su kien.
    if (tool === 'note' || tool === 'freetext') return 'note';
    if (tool === 'select') return 'select';
    // Cong cu "Sửa chữ": lop chu thich phai trong suot hoan toan, moi cu bam
    // thuoc ve van ban ben duoi.
    return 'none';
  }, [tool, originalPageIndex]);

  // =========================================================================
  //  ANH DA NHUNG SAN
  // =========================================================================
  //  CHI bat khi cong cu la "Chọn". Voi moi cong cu khac, cu bam thuoc ve viec
  //  khac (ve hinh, dat ghi chu, sua chu), va bat anh o day se cuop mat no.
  const imageToolActive = embeddedImages !== undefined && tool === 'select' && editable;
  const imageState = useEmbeddedImages(
    core,
    doc,
    imageToolActive ? originalPageIndex : null,
    embeddedImages?.commands ?? EMPTY_COMMANDS,
    imageToolActive,
  );
  const imageDrag = useImageDragOverlay();

  /**
   * Anh KEM vi tri tam trong luc keo.
   *
   * Ban do keo de len ket qua chieu tu danh sach lenh. Khi nha tay, lenh duoc
   * ghi nhan va ban do bi xoa - hai nguon nhap lai lam mot ngay trong khung
   * hinh do. Giu ban do lai sau khi da co lenh se lam anh dung yen o vi tri cu
   * neu nguoi dung bam Ctrl+Z.
   */
  const imageViews = useMemo((): EmbeddedImageView[] => {
    return imageState.images.map((img) => ({
      id: `p${img.pageIndex}-img${img.occurrence}`,
      rect: imageDrag.overlay.get(`p${img.pageIndex}-img${img.occurrence}`) ?? img.rect,
      // Khong doc duoc doan byte dat anh thi khong keo duoc. Ly do cua ca trang
      // (neu co) thang ly do cua rieng mot anh.
      refusal:
        imageState.refusal ??
        (img.localMatrix === null
          ? 'Không xác định được đoạn nào trong luồng nội dung đặt ảnh này, nên bản web không ' +
            'di chuyển hay xóa nó được. Cần lõi Rust/PDFium.'
          : // Ly do RIENG cua anh nay - hien tai la truong hop bi duong cat bao
            // quanh. Xet SAU ly do cua ca trang va sau truong hop khong doc duoc
            // byte, vi hai thu do chan nang hon (khong xoa duoc), con day chi
            // chan di chuyen.
            (img.transformRefusal ?? null)),
    }));
  }, [imageState.images, imageState.refusal, imageDrag.overlay]);

  /** Doi tuong dang chon chi "thuoc ve" trang nay khi no nam DUNG tren no. */
  const selectedHereId = useMemo(() => {
    if (!selectedAnnotationId) return null;
    const found = annotations.find((a) => a.id === selectedAnnotationId);
    return found && found.pageIndex === originalPageIndex ? found.id : null;
  }, [selectedAnnotationId, annotations, originalPageIndex]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Viec BO CHON da thuoc ve lop chu thich: no la ben duy nhat biet cu bam
      // co trung mot doi tuong hay khong (pickAt), va no chi tha su kien xuong
      // day khi da khong trung gi ca. Lam lai o day se bo chon ngay ca khi lop
      // tren vua chon duoc mot doi tuong.
      const host = event.currentTarget.getBoundingClientRect();
      const px = event.clientX - host.left;
      const py = event.clientY - host.top;
      const point = cssPointToPdf(px, py, geometry);

      // --- ANH DA NHUNG SAN, truoc van ban ---
      //
      // Truoc van ban vi mot anh CHE MAT chu nam duoi no: neu van ban duoc xet
      // truoc, bam vao giua mot anh phu len doan chu se chon doan chu vo hinh
      // ben duoi - mot lua chon nguoi dung khong nhin thay va khong hieu.
      if (imageToolActive && embeddedImages) {
        const hit = pickImageAt(imageState.images, point);
        if (hit) {
          embeddedImages.onSelect(hit);
          // NOI RA NGAY LUC CHON, khong doi den luc keo. Mot anh chon duoc ma
          // khong co tay nam, khong kem loi giai thich, trong y het mot tay nam
          // hong - nguoi dung se keo vai lan roi ket luan la phan mem loi.
          const why = imageState.refusal ?? hit.transformRefusal ?? null;
          if (why) embeddedImages.onRefusal?.(why);
          return;
        }
        // Bam truot moi anh TREN MOT TRANG CO DO HOA VECTO: noi that ra. Im
        // lang o day nghia la nguoi dung bam vao cai logo, khong co gi xay ra,
        // va ho khong biet la ban web khong lam duoc hay ho bam truot.
        if (embeddedImages.selectedId !== null) embeddedImages.onSelect(null);
        else if (imageState.vectorNote) embeddedImages.onVectorNote?.(imageState.vectorNote);
      }

      if (blocks.length === 0) return;

      for (let i = blocks.length - 1; i >= 0; i--) {
        const block = blocks[i];
        if (!block) continue;
        const r = pdfRectToCss(block.bbox, geometry);
        // Noi rong vung bam mot chut: hop bao chu thuong sat khit.
        const pad = 2;
        if (
          px >= r.left - pad &&
          px <= r.left + r.width + pad &&
          py >= r.top - pad &&
          py <= r.top + r.height + pad
        ) {
          // Diem bam da o trong he toa do PDF, nen viec chon pham vi khong phai
          // biet gi ve zoom hay do xoay - toan bo phep doi he nam trong
          // geometry.ts, dung mot cho nhu quy uoc cua du an.
          const scope = scopeAtPoint(block, point);
          onSelectBlock(block, scope);
          if (tool === 'text' && scope) onRequestEdit(block, scope);
          return;
        }
      }
      onSelectBlock(null, null);
    },
    [
      blocks,
      geometry,
      onSelectBlock,
      tool,
      onRequestEdit,
      imageToolActive,
      embeddedImages,
      imageState.images,
      imageState.refusal,
      imageState.vectorNote,
    ],
  );

  return (
    <div
      className={`pageView${tool === 'text' ? ' textTool' : ''}`}
      data-page-index={slotIndex}
      data-source-page={originalPageIndex ?? ''}
      style={{ width: `${size.width}px`, height: `${size.height}px` }}
      onPointerDown={handlePointerDown}
    >
      <div className="pageSurface">
        {/*
          TRANG TRANG khong co doi tuong trang de ve, va cung KHONG duoc muon
          canvas: mot canvas trang la 10-30 MB bitmap cho dung mot mang mau
          trang. No la mot the div, dung kich thuoc ma duong xuat se ghi ra.
        */}
        {isBlank ? (
          <div className="pageBlank" aria-hidden="true" />
        ) : (
          <CanvasLayer
            page={page}
            cssWidth={size.width}
            cssHeight={size.height}
            zoom={geometry.zoom}
            draft={draft}
            rotation={geometry.rotation}
            revision={editRevision}
          />
        )}
        <EditedTextLayer
          blocks={blocks}
          edited={edited}
          hints={editHints}
          geometry={geometry}
        />
        {hasTextLayer && !isBlank && (
          <TextLayer
            page={page}
            zoom={geometry.zoom}
            rotation={geometry.rotation}
            enabled={active && !draft}
          />
        )}
        {blockOutlineRect && (
          <div
            className="blockOutline"
            aria-hidden="true"
            style={{
              left: `${blockOutlineRect.left}px`,
              top: `${blockOutlineRect.top}px`,
              width: `${blockOutlineRect.width}px`,
              height: `${blockOutlineRect.height}px`,
            }}
          />
        )}
        {/*
          O CHON, TAY NAM VA MOI THAO TAC TREN DOI TUONG deu nam trong lop nay.

          Truoc day vien chon duoc ve o day bang mot the div rieng, voi tam tay
          nam chi la trang tri: chung khong keo duoc, va chung lay hop chu nhat
          bang mot phep tinh khac voi phep tinh cua lop chu thich. Bay gio o chon
          duoc ve BEN TRONG lop bat thao tac, tu chinh hinh chu nhat ma
          resolveResize() se giao cho lenh - mot phep tinh, khong con cho nao de
          hai ban troi khoi nhau.
        */}
        <AnnotationLayer
          annotations={annotations}
          geometry={geometry}
          highlightRect={highlightRect}
          capture={capture}
          {...(originalPageIndex === null
            ? {}
            : {
                pageIndex: originalPageIndex,
                onInkStroke,
                onNotePoint,
                onShape: (page: number, kind: ShapeTool, rect: PdfRect) =>
                  onShape?.(kind, page, rect),
                onImageRect,
              })}
          imageUrl={imageUrl}
          onSelect={onSelectAnnotation}
          selectedId={selectedHereId}
          onDeselect={onDeselectAnnotation}
          onChangeRect={onChangeAnnotationRect}
          onDelete={onDeleteAnnotation}
        />
        {/*
          LOP ANH nam TREN lop chu thich, va no trong suot voi chuot tru dung
          vung tay nam cua anh dang chon - xem khoi ghi chu dau
          EmbeddedImageLayer.tsx. Dat duoi lop chu thich thi tay nam se bi lop
          kia nuot mat cu bam.
        */}
        {imageToolActive && embeddedImages && imageViews.length > 0 && (
          <EmbeddedImageLayer
            images={imageViews}
            geometry={geometry}
            selectedId={embeddedImages.selectedId}
            onChangeRect={imageDrag.setRect}
            onCommitRect={(id, rect) => {
              const found = imageState.images.find(
                (i) => `p${i.pageIndex}-img${i.occurrence}` === id,
              );
              imageDrag.clear(id);
              if (found) embeddedImages.onCommitRect(found, rect);
            }}
          />
        )}
        {!page && !isBlank && <div className="pagePlaceholder" aria-hidden="true" />}
      </div>

      {/*
        O soan duoc dat va do co chu theo PHAM VI, khong theo khoi cha. Neu lay
        theo khoi cha, o soan mot o bang se phu ca bang - va nguoi dung se tuong
        minh sap thay ca bang, dung noi so ma tinh nang nay sinh ra de go bo.
      */}
      {editSession && editingScope && (
        <InlineTextEditor
          session={editSession}
          rect={pdfRectToCss(editingScope.bbox, geometry)}
          fontSizePx={Math.max(1, runsFontSizePt(editingScope.runs)) * geometry.zoom}
          colorCss={cssColor(runsColorRgb(editingScope.runs))}
        />
      )}

      {noteDraft && noteDraft.pageIndex === originalPageIndex && (
        <NotePopup
          // Doi o chu giai khi doi ghi chu: key ep React dung mot the hien MOI,
          // nen ban nhap cua ghi chu truoc khong bam sang ghi chu vua bam vao.
          key={noteDraft.id ?? 'new'}
          left={
            pdfRectToCss({ x: noteDraft.point.x, y: noteDraft.point.y, width: 0, height: 0 }, geometry)
              .left
          }
          top={
            pdfRectToCss({ x: noteDraft.point.x, y: noteDraft.point.y, width: 0, height: 0 }, geometry)
              .top
          }
          value={noteDraft.value}
          title={noteDraft.kind === 'free-text' ? 'Hộp chữ' : 'Ghi chú'}
          // Kieu chu LIVE do App giu (bang thuoc tinh o popup sua), lui ve style
          // ban dau cua o soan neu chua truyen xuong. O go chi ve theo no.
          style={noteStyle ?? noteDraft.style}
          onAlignChange={(align) => onNoteAlignChange?.(align)}
          zoom={geometry.zoom}
          onSave={(text) => onNoteSave?.(text)}
          onCancel={() => onNoteCancel?.()}
        />
      )}

      {/* So trang la vi tri trong SO DO, khong phai chi so trong tep goc. */}
      <div className="pageLabel">{slotIndex + 1}</div>
    </div>
  );
}
